import React from "react";

import { formatCost } from "src/base-components/agents/MetricPills";
import {
  ExecutionMetricsResponse,
  SessionResponse,
  UsageMetricsResponse,
} from "src/clients/agent-management";
import { formatDuration, formatNumber } from "src/utils/numbers";

type Stat = { label: string; value: string };

const formatPercentage = (percentage: number): string =>
  `${formatNumber(percentage, { maximumFractionDigits: 1 })}%`;

// Exact counts, not the compact "1.2k" the history table shows: a debug reader
// is comparing token numbers against a model's context window.
const countStat = (label: string, value: number | null | undefined): Stat[] =>
  value == null ? [] : [{ label, value: formatNumber(value) }];

const durationStat = (
  label: string,
  valueMs: number | null | undefined,
): Stat[] =>
  valueMs == null ? [] : [{ label, value: formatDuration(valueMs) }];

const contextStat = (
  label: string,
  tokens: number | null | undefined,
  usagePct: number,
): Stat => ({
  label,
  value:
    tokens == null
      ? formatPercentage(usagePct)
      : `${formatNumber(tokens)} (${formatPercentage(usagePct)})`,
});

const sessionStats = (session: SessionResponse): Stat[] => [
  ...(session.model == null ? [] : [{ label: "Model", value: session.model }]),
  { label: "Mode", value: session.session_mode },
  { label: "Environment", value: session.environment },
  { label: "Trigger", value: session.trigger.source_type },
  { label: "Agent version", value: session.agent_version_id },
];

const usageStats = (usage: UsageMetricsResponse): Stat[] => [
  ...countStat("Total tokens", usage.total_tokens),
  ...countStat("Input", usage.input_tokens),
  ...countStat("Output", usage.output_tokens),
  ...countStat("Reasoning", usage.reasoning_tokens),
  ...(usage.total_cost_usd == null
    ? []
    : [{ label: "Cost", value: formatCost(usage.total_cost_usd) }]),
  ...countStat("Context window", usage.context_window_size),
  contextStat(
    "Context now",
    usage.current_context_tokens,
    usage.current_context_usage_pct,
  ),
  contextStat(
    "Context peak",
    usage.peak_context_tokens,
    usage.peak_context_usage_pct,
  ),
];

const executionStats = (execution: ExecutionMetricsResponse): Stat[] => [
  ...countStat("LLM calls", execution.llm_call_count),
  ...countStat("Tool calls", execution.tool_call_count),
  ...countStat("User tool calls", execution.user_tool_call_count),
  ...countStat("Iterations", execution.num_iterations),
  ...durationStat("Active runtime", execution.active_runtime_ms),
  ...durationStat("Session duration", execution.session_duration_ms),
];

const runtimeByCategoryStats = (execution: ExecutionMetricsResponse): Stat[] =>
  Object.entries(execution.runtime_by_category ?? {}).map(
    ([category, summary]) => ({
      label: category,
      value: [
        ...(summary.total_ms == null ? [] : [formatDuration(summary.total_ms)]),
        ...(summary.count == null ? [] : [`${formatNumber(summary.count)}x`]),
      ].join(" · "),
    }),
  );

const StatGroup: React.FC<{
  dataLoc: string;
  stats: Stat[];
  title: string;
}> = ({ dataLoc, stats, title }) =>
  stats.length === 0 ? null : (
    <div className="flex flex-col gap-1" data-loc={dataLoc}>
      <span className="font-inter-semibold-12px text-gray-800">{title}</span>
      <dl className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {stats.map(({ label, value }) => (
          <div className="flex items-center gap-2" key={label}>
            <dt className="font-inter-normal-12px text-gray-500">{label}</dt>
            <dd className="font-inter-normal-12px text-gray-800">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

export const SessionMetricsPanel: React.FC<{ session: SessionResponse }> = ({
  session,
}) => (
  <div
    className="mt-4 flex shrink-0 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4"
    data-loc="session-debug-metrics"
  >
    <StatGroup
      dataLoc="session-debug-metrics-session"
      stats={sessionStats(session)}
      title="Session"
    />
    <StatGroup
      dataLoc="session-debug-metrics-usage"
      stats={usageStats(session.metrics.usage)}
      title="Usage"
    />
    <StatGroup
      dataLoc="session-debug-metrics-execution"
      stats={executionStats(session.metrics.execution)}
      title="Execution"
    />
    <StatGroup
      dataLoc="session-debug-metrics-runtime"
      stats={runtimeByCategoryStats(session.metrics.execution)}
      title="Runtime by category"
    />
  </div>
);
